import { Link } from 'react-router-dom';
import { Button } from '@beatq/ui';
import type { BranchSummary } from '../branch-settings/branchSettingsApi.js';
import type { MerchantInboxMessage, MerchantInboxMeta } from './merchantNotificationsApi.js';
import {
  groupMerchantNotifications,
  humanizeSubjectKey,
  merchantMessageBranchLabel,
  formatMerchantInboxTime,
} from './merchantInbox.js';

export function NotificationInbox({
  messages,
  meta,
  branches,
  branchFilter,
  onBranchFilter,
  unreadOnly,
  onUnreadOnly,
  categoryFilter,
  onCategoryFilter,
  categories,
  onMarkRead,
  onMarkAll,
  markReadPending,
  markAllPending,
  cachedUnread,
}: {
  messages: MerchantInboxMessage[];
  meta: MerchantInboxMeta;
  branches: BranchSummary[];
  branchFilter: string;
  onBranchFilter: (value: string) => void;
  unreadOnly: boolean;
  onUnreadOnly: (value: boolean) => void;
  categoryFilter: string;
  onCategoryFilter: (value: string) => void;
  categories: string[];
  onMarkRead: (id: string) => void;
  onMarkAll: () => void;
  markReadPending: boolean;
  markAllPending: boolean;
  cachedUnread?: number;
}) {
  const grouped = groupMerchantNotifications(messages);
  const unread = meta.unreadCount ?? cachedUnread;
  const emptyFilterLabel = [
    unreadOnly ? 'unread' : null,
    categoryFilter || null,
    branchFilter ? branches.find((b) => b.id === branchFilter)?.name || 'this branch' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="m01-inbox" data-testid="merchant-inbox">
      <p className="text-muted mb-field" data-testid="merchant-inbox-meta">
        {unread != null ? `${unread} unread` : 'Inbox'}
        {' · '}
        Last 24 months
      </p>
      <div className="m01-inbox-toolbar">
        <label className="m01-inbox-filter">
          <span>Branch</span>
          <select
            className="mer-store-select"
            value={branchFilter}
            onChange={(e) => onBranchFilter(e.target.value)}
            data-testid="merchant-inbox-branch-filter"
            aria-label="Filter notifications by branch"
          >
            <option value="">All scoped branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name || branch.publicReference || branch.id}
              </option>
            ))}
          </select>
        </label>
        <label className="m01-inbox-unread">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => onUnreadOnly(e.target.checked)}
            data-testid="merchant-inbox-unread-only"
          />
          Unread only
        </label>
        <label className="m01-inbox-filter">
          <span>Category</span>
          <select
            className="mer-store-select"
            value={categoryFilter}
            onChange={(e) => onCategoryFilter(e.target.value)}
            data-testid="merchant-inbox-category-filter"
            aria-label="Filter notifications by category"
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>
        {messages.length > 0 ? (
          <Button
            type="button"
            variant="secondary"
            className="min-h-12"
            disabled={markAllPending || (unread ?? 0) === 0}
            onClick={onMarkAll}
          >
            Mark all read
          </Button>
        ) : null}
      </div>
      {messages.length === 0 ? (
        <p className="text-muted" data-testid="merchant-inbox-empty">
          {emptyFilterLabel
            ? `No notifications match ${emptyFilterLabel}.`
            : 'No notifications to show.'}
        </p>
      ) : (
        [...grouped.entries()].map(([subject, items]) => (
          <section key={subject} className="m01-inbox-group">
            <h2 className="m01-inbox-group-title">{humanizeSubjectKey(subject)}</h2>
            <ul className="mer-inbox-list" data-testid="merchant-inbox-list">
              {items.map((message) => {
                const branchLabel = merchantMessageBranchLabel(message, branches);
                const when = formatMerchantInboxTime(message.createdAt ?? message.created_at);
                return (
                  <li
                    key={message.id}
                    className="mer-inbox-item"
                    data-read={message.isRead ? 'true' : 'false'}
                  >
                    <div>
                      <strong>{message.title ?? message.body ?? 'Untitled'}</strong>
                      {branchLabel ? ` · ${branchLabel}` : ''}
                    </div>
                    {message.body && message.title ? (
                      <p className="text-muted">{message.body}</p>
                    ) : null}
                    {when ? <p className="text-muted">{when}</p> : null}
                    <div className="mer-inbox-item-actions">
                      {!message.isRead ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="min-h-11"
                          disabled={markReadPending}
                          onClick={() => onMarkRead(message.id)}
                        >
                          Mark read
                        </Button>
                      ) : (
                        <span className="text-muted">Read</span>
                      )}
                      {message.deepLink ? <Link to={message.deepLink}>Open</Link> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
